import { useEffect, useMemo, useRef } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { routes } from '@platform/core/lib/routes';
import { DeleteModuleButton, isCoreEntity, RecordTypeBuilder } from '@platform/features/workspaces/builder';
import { useGetModuleQuery } from '@platform/state/api';
import { Spinner } from '@platform/ui';
import { appRoutes } from '../../shell/appRoutes';
import { BuilderModuleList } from './BuilderModuleList';
import { builderTypeName } from './builderTypeName';
import { builderTypes } from './builderTypes';
import { useNewCustomModule } from './useNewCustomModule';
import { useWorkspaceDefinitions } from './useWorkspaceDefinitions';
import './customize.css';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';

/**
 * «Конструктор модулів» (прототип v4 page==='builder', cust): колонка «Визначення модулів» (кожен тип запису простору в порядку прототипу,
 * «+ Новий модуль» — власний модуль простору, ADR-025) і вибраний тип — назва з позначкою «Основна сутність» / «Власний модуль»,
 * «Шаблон, що визначає форму та кожне подання записів модуля.», «Видалити модуль» (неосновні модулі; після нього — «Справи», як у прототипі),
 * «Відкрити модуль →» і конструктор (RecordTypeBuilder: визначення простору, що зберігається одразу — ADR-023).
 */
export function CustBuilderPage() {
  const { t } = useTranslation();
  const label = useLabel();
  const navigate = useNavigate();
  const { item } = useParams();
  const { ws } = useWorkspaceParams();
  const { catalog, definitions } = useWorkspaceDefinitions(ws, false);
  const types = useMemo(() => builderTypes(definitions ?? [], catalog), [definitions, catalog]);
  const created = useNewCustomModule(ws);
  const row = types.find((r) => r.rt.key === item) ?? (created.pending === item ? undefined : types[0]);
  const moduleKey = row?.def.module.key ?? '';
  const { data: module } = useGetModuleQuery({ key: moduleKey, workspaceId: ws }, { skip: !moduleKey });
  const list = useRef<HTMLDivElement>(null);
  // новий модуль — наприкінці переліку: прокрутити до вибраного, як прототип після «+ Новий модуль»
  useEffect(() => { list.current?.querySelector('.cu-builder__item.is-active')?.scrollIntoView({ block: 'nearest' }); }, [row?.rt.key]);
  if (!definitions || (!row && created.pending)) return <Spinner />;
  if (!row) return null;
  if (item !== row.rt.key) return <Navigate to={appRoutes.customize('builder', row.rt.key)} replace />;
  // прототип v4 openBMod: повернення до модуля в його поданні й пункті меню (без скидання), дочірня таблиця — її список
  const open = row.rt.main ? routes.moduleReturn(moduleKey) : routes.list(moduleKey, row.rt.key);
  return (
    <div className="cu-builder" ref={list}>
      <BuilderModuleList catalog={catalog} types={types} current={row.rt.key} onCreate={created.create} canCreate={created.canCreate} creating={created.busy} />
      <div className="cu-builder__main">
        <div className="cu-builder__head">
          <div className="cu-builder__titles">
            <div className="cu-builder__titleRow">
              <h1 className="cu-builder__title">{builderTypeName(row, catalog, label)}</h1>
              <span className="cu-builder__kind">{t(isCoreEntity(row.def.module, row.rt) ? 'sysCust.builder.mainEntity' : 'sysCust.builder.customModule')}</span>
            </div>
            <div className="sp-muted">{t('sysCust.builder.sub')}</div>
          </div>
          <div className="cu-builder__actions">
            <DeleteModuleButton ws={ws} moduleKey={moduleKey} onDeleted={() => navigate(appRoutes.customize('builder', 'case'))} />
            <Link className="cu-builder__open" to={open}>{t('sysCust.builder.openModule')}</Link>
          </div>
        </div>
        {module ? <RecordTypeBuilder ws={ws} module={module} rtKey={row.rt.key} layoutTo={appRoutes.customize('layout')} /> : <Spinner />}
      </div>
    </div>
  );
}
